<script>
	//@ts-check
	import { onMount } from 'svelte';
	import { page } from '$app/stores';
	import ky from 'ky';
	import BmMarkIcon from '$lib/components/BmMarkIcon.svelte';
	import { uuidv4, getfetchopts } from '$lib/utils/utils.js';
	import { store } from '$lib/utils/store.js';
	import { validateAuth } from '$lib/stores/auth.svelte.js';

	//Both sign-in methods are equal peers — share one class string so neither
	//button reads as the preferred default.
	const signinButtonClass =
		'group flex w-full items-center justify-between gap-3 rounded-lg bg-gradient-to-r from-(--color-appbttn1-grad1) to-(--color-appbttn1-grad2) px-6 py-4 text-left font-medium text-white transition-all hover:from-(--color-appbttn1-active-grad1) hover:to-(--color-appbttn1-active-grad2) focus:ring-2 focus:ring-(--color-appbttn1-grad1) focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60';

	let redirect = $state('/');
	let isLoading = $state(false);
	let isCodeLoading = $state(false);
	let isInitializing = $state(true);
	let error = $state('');

	onMount(async () => {
		const isValid = await validateAuth();
		if (isValid) {
			const dest = $page.url.searchParams.get('redirect') || '/';
			window.location.href = dest;
			return;
		}

		redirect = $page.url.searchParams.get('redirect') || '/';
		isInitializing = false;
	});

	async function handlePasswordLogin() {
		if (isLoading) return;
		isLoading = true;
		error = '';

		try {
			const ddata = await store('device');
			const devicedata = ddata ? ddata : {};
			if (!devicedata.device_id) {
				devicedata.device_id = uuidv4();
				await store('device', devicedata);
			}

			await store('authdest', redirect);

			//in localhost dev, tell the API to bounce back to localhost after login
			//instead of the lab/dev my.* domain. Backend re-validates this.
			const payload = { returnUrl: redirect, device_id: devicedata.device_id };
			if (import.meta.env.DEV && window.location.origin.startsWith('http://localhost')) {
				payload.localOrigin = window.location.origin;
			}
			const fetchopts = await getfetchopts({ json: payload });
			const result = await ky.post('/api/identity/auth/create-login-state', fetchopts).json();
			console.info('create-login-state result', { ok: result.ok, hasLoginUrl: !!result.loginUrl });
			if (!result.ok || !result.loginUrl) {
				throw new Error('Failed to initiate login. Please try again.');
			}
			window.location.href = result.loginUrl;
		} catch (e) {
			console.error('Error initiating login', e);
			error = e.message || 'Failed to initiate login. Please try again.';
			isLoading = false;
		}
	}

	async function handleCodeLogin() {
		if (isLoading || isCodeLoading) return;
		isCodeLoading = true;
		error = '';

		try {
			const ddata = await store('device');
			const devicedata = ddata ? ddata : {};
			if (!devicedata.device_id) {
				devicedata.device_id = uuidv4();
				await store('device', devicedata);
			}

			await store('authdest', redirect);

			//in localhost dev, tell the API to bounce back to localhost after login
			//instead of the lab/dev my.* domain. Backend re-validates this.
			const payload = { returnUrl: redirect, device_id: devicedata.device_id };
			if (import.meta.env.DEV && window.location.origin.startsWith('http://localhost')) {
				payload.localOrigin = window.location.origin;
			}
			const fetchopts = await getfetchopts({ json: payload });
			const result = await ky.post('/api/identity/auth/create-login-state', fetchopts).json();
			console.info('create-login-state result', { ok: result.ok, hasLoginUrl: !!result.loginUrl });
			if (!result.ok || !result.loginUrl) {
				throw new Error('Failed to initiate login. Please try again.');
			}
			//same LoginState/state as the password path; only the login-domain page
			//that consumes the state differs — point at the code-entry page.
			const u = new URL(result.loginUrl); //preserves ?state= and ?t=
			u.pathname = '/codelogin/';
			window.location.href = u.toString();
		} catch (e) {
			console.error('Error initiating code login', e);
			error = e.message || 'Failed to initiate login. Please try again.';
			isCodeLoading = false;
		}
	}
</script>

<svelte:head>
	<title>Sign in to BusinessMind</title>
</svelte:head>

{#if isInitializing}
	<div class="flex min-h-screen items-center justify-center bg-(--color-appbg)">
		<div class="text-center">
			<i class="fa-solid fa-spinner fa-spin mb-4 text-4xl text-(--color-appbttn1-grad1)"></i>
			<p class="text-(--color-apptext-secondary)">Loading...</p>
		</div>
	</div>
{:else}
	<div class="flex min-h-screen items-center justify-center bg-(--color-appbg) px-4 py-12">
		<div class="w-full max-w-md">
			<div class="mb-8 text-center">
				<div class="mb-4 flex justify-center">
					<BmMarkIcon class="h-16 w-16 text-(--color-appbttn1-grad1)" />
				</div>
				<h1 class="mb-2 text-3xl font-bold text-(--color-apptext-primary)">BusinessMind</h1>
				<p class="text-sm text-(--color-apptext-secondary)">Choose how you'd like to sign in</p>
			</div>

			<div class="rounded-xl border border-(--color-appcard-border) bg-(--color-appcard) p-6 shadow-lg sm:p-8">
				{#if error}
					<div class="mb-4 rounded-lg bg-(--color-error-50) p-3 text-sm text-(--color-error-800)">{error}</div>
				{/if}

				<div class="space-y-3">
					<button
						type="button"
						onclick={handlePasswordLogin}
						disabled={isLoading || isCodeLoading}
						class={signinButtonClass}
					>
						<div class="flex items-center gap-3">
							{#if isLoading}
								<i class="fa-solid fa-spinner fa-spin text-xl"></i>
							{:else}
								<i class="fa-solid fa-key text-xl"></i>
							{/if}
							<div>
								<div class="font-semibold">Password Login</div>
								<div class="text-sm opacity-90">
									{isLoading ? 'Connecting...' : 'Sign in with email and password'}
								</div>
							</div>
						</div>
						{#if !isLoading}
							<i class="fa-solid fa-arrow-right text-lg transition-transform group-hover:translate-x-1"></i>
						{/if}
					</button>

					<button
						type="button"
						onclick={handleCodeLogin}
						disabled={isLoading || isCodeLoading}
						class={signinButtonClass}
					>
						<div class="flex items-center gap-3">
							{#if isCodeLoading}
								<i class="fa-solid fa-spinner fa-spin text-xl"></i>
							{:else}
								<i class="fa-solid fa-envelope text-xl"></i>
							{/if}
							<div>
								<div class="font-semibold">Email Code</div>
								<div class="text-sm opacity-90">
									{isCodeLoading ? 'Connecting...' : 'Receive a one-time code by email'}
								</div>
							</div>
						</div>
						{#if !isCodeLoading}
							<i class="fa-solid fa-arrow-right text-lg transition-transform group-hover:translate-x-1"></i>
						{/if}
					</button>
				</div>

				<div class="mt-6 flex items-start gap-3 rounded-lg border border-(--color-appcard-border) bg-(--color-appcard-accent) p-4">
					<i class="fa-solid fa-circle-info mt-0.5 text-(--color-appbttn1-grad1)"></i>
					<p class="text-sm text-(--color-apptext-secondary)">
						You'll be redirected to a secure sign-in page. The session expires after 5 minutes.
					</p>
				</div>
			</div>

			<div class="mt-6 text-center text-sm text-(--color-apptext-secondary)">
				<p>
					Don't have an account?
					<a href="/signup/" class="font-medium text-(--color-apptext-title) hover:underline">Sign up</a>
				</p>
			</div>

			<div class="mt-8 text-center text-xs text-(--color-apptext-tertiary)">
				<p>&copy; {new Date().getFullYear()} BusinessMind</p>
			</div>
		</div>
	</div>
{/if}
